import { makeStyles } from '@material-ui/styles'
import React, { useCallback, useMemo } from 'react'
import { useSelector } from 'react-redux'
import type { Host } from 'api-client/resmgr.model'
import { isAdmin } from 'app/plugins/infrastructure/components/common/helpers'
import { listNetworkServiceDetails } from 'app/plugins/infrastructure/components/common/service-health/action'
import { useControlPlaneServicesCheck } from 'app/plugins/infrastructure/components/common/service-health/ControlPlaneServicesContext'
import { validServiceDetails } from 'app/plugins/infrastructure/components/common/service-health/helper'
import { networkServicesSelector } from 'app/plugins/infrastructure/components/common/service-health/selector'
import { getRelevantServiceTypes } from 'app/plugins/infrastructure/components/common/service-health/serviceFeatureMap'
import FontAwesomeIcon from 'core/components/FontAwesomeIcon'
import SimpleLink from 'core/components/SimpleLink'
import Info from 'core/components/validatedForm/Info'
import useListAction from 'core/hooks/useListAction'
import type Theme from 'core/themes/model'
import { routeManager } from 'core/utils/routes'
import { listResmgrHosts } from 'openstack/components/infrastructure/actions'
import {
  ControlPlaneServiceUnhealthyMessage,
  getWizardAlertMessage,
  hasCinderRole,
  hasHypervisorRole,
  hasImageLibraryRole,
  hostUnhealthy,
} from 'openstack/components/infrastructure/helpers'
import { resmgrHostsSelector } from 'openstack/components/infrastructure/selectors'
import { IHealthServiceDetailsPageTabs } from '../networks/model'

const useStyles = makeStyles<Theme>((theme: Theme) => ({
  root: {
    marginBottom: 16,
  },
  icon: {
    marginRight: 8,
  },
}))

interface Props {
  hypervisor?: boolean
  imageLibrary?: boolean
  imageLibraryEndpoint?: 'public' | 'admin'
  blockStorage?: boolean
  network?: boolean
  action: string
}

export default function ServiceUnhealthyInfo({
  hypervisor = false,
  imageLibrary = false,
  imageLibraryEndpoint,
  blockStorage = false,
  network = false,
  action,
}: Props) {
  const classes = useStyles()
  const { loading: loadingResmgrHosts } = useListAction(listResmgrHosts)
  const resmgrHosts = useSelector(resmgrHostsSelector)

  const { loading: loadingNetworkServices } = useListAction(listNetworkServiceDetails)
  const networkServiceDetails = useSelector(networkServicesSelector)
  const validNetworkServiceDetails = validServiceDetails(networkServiceDetails)

  // Check status of control plane services - get both aggregate states AND individual service list
  const { serviceHealthStates: controlPlaneServicesStates, servicesList } =
    useControlPlaneServicesCheck()

  // Determine which service types are relevant based on the feature flags passed to this component
  const relevantServiceTypes = useMemo(() => {
    return getRelevantServiceTypes({
      hypervisor,
      imageLibrary,
      imageLibraryEndpoint,
      blockStorage,
      network,
    })
  }, [hypervisor, imageLibrary, imageLibraryEndpoint, blockStorage, network])

  // Filter unhealthy services to only those relevant to the current operation
  // This ensures we only show warnings for services that matter for the action being performed
  const relevantUnhealthyServices = useMemo(() => {
    if (!servicesList?.length) return []

    return servicesList.filter(
      (service) => relevantServiceTypes.includes(service.type) && service.status !== 'Healthy',
    )
  }, [servicesList, relevantServiceTypes])

  // Check only relevant control plane services instead of all services
  const hasRelevantControlPlaneIssues = relevantUnhealthyServices.length > 0

  // Function to check status of OpenStack services
  const checkServiceUnhealthy = useCallback(
    (
      hosts: Host[],
      serviceFilterFunction: (host: Host) => boolean,
      hostFilterFunction = hostUnhealthy,
    ): boolean => {
      const matchingHosts = hosts?.filter((host) => serviceFilterFunction(host))
      if (!matchingHosts?.length) {
        return false
      }

      const unhealthyHosts = matchingHosts?.filter((host) => hostFilterFunction(host))
      const mostHostsUnhealthy = matchingHosts?.length / 2 < unhealthyHosts?.length

      return matchingHosts?.length && mostHostsUnhealthy
    },
    [hostUnhealthy],
  )

  const unhealthyHosts = useMemo(() => {
    const defaultUnhealthyHosts = {
      hypervisor: false,
      imageLibrary: false,
      blockStorage: false,
      network: false,
    }
    if (!resmgrHosts.length) {
      return defaultUnhealthyHosts
    }

    // Check hypervisor status
    if (hypervisor) {
      defaultUnhealthyHosts.hypervisor = checkServiceUnhealthy(resmgrHosts, hasHypervisorRole)
    }

    // Check image library status
    if (imageLibrary) {
      defaultUnhealthyHosts.imageLibrary = checkServiceUnhealthy(resmgrHosts, hasImageLibraryRole)
    }

    // Check block storage status
    if (blockStorage) {
      defaultUnhealthyHosts.blockStorage = checkServiceUnhealthy(resmgrHosts, hasCinderRole)
    }

    // Check network status
    if (network) {
      if (!validNetworkServiceDetails?.length) {
        return defaultUnhealthyHosts
      }

      const unhealthyNetworkNodes = validNetworkServiceDetails?.filter(
        (service) => service?.status !== 'Healthy',
      )
      const mostNetworkNodesUnhealthy =
        validNetworkServiceDetails?.length / 2 < unhealthyNetworkNodes?.length

      defaultUnhealthyHosts.network =
        validNetworkServiceDetails?.length && mostNetworkNodesUnhealthy
    }

    return defaultUnhealthyHosts
  }, [resmgrHosts, validNetworkServiceDetails, hypervisor, imageLibrary, blockStorage, network])

  const message = getWizardAlertMessage({
    hypervisor: hypervisor && unhealthyHosts.hypervisor,
    imageLibrary: imageLibrary && unhealthyHosts.imageLibrary,
    blockStorage: blockStorage && unhealthyHosts.blockStorage,
    network: network && unhealthyHosts.network,
    action,
  })

  return message ? (
    <Info className={classes.root} error>
      <FontAwesomeIcon className={classes.icon}>triangle-exclamation</FontAwesomeIcon>
      {message}
    </Info>
  ) : hasRelevantControlPlaneIssues ? (
    // Only show control plane warning if relevant services are unhealthy
    // This prevents showing warnings for services unrelated to the current operation
    <Info className={classes.root} error>
      <FontAwesomeIcon className={classes.icon}>triangle-exclamation</FontAwesomeIcon>
      {ControlPlaneServiceUnhealthyMessage}{' '}
      {isAdmin() && (
        <SimpleLink
          src={routeManager.routes.openstack.health.details.path({
            tab: IHealthServiceDetailsPageTabs.ControlPlaneServicesHealth,
          })}
        >
          <b>See Details</b>
        </SimpleLink>
      )}
    </Info>
  ) : null
}
